import React, { useRef, useMemo, useState } from 'react';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import type { CKEditor as CKEditorType } from '@ckeditor/ckeditor5-react';
import {
  ClassicEditor,
  Alignment,
  Autoformat,
  AutoImage,
  Autosave,
  BalloonToolbar,
  BlockQuote,
  Bold,
  Code,
  // CodeBlock,
  Emoji,
  Essentials,
  FindAndReplace,
  FontBackgroundColor,
  FontColor,
  FontFamily,
  FontSize,
  Fullscreen,
  Heading,
  Highlight,
  HorizontalLine,
  // HtmlEmbed,
  ImageBlock,
  ImageCaption,
  ImageInline,
  ImageInsert,
  // ImageInsertViaUrl,
  ImageResize,
  ImageStyle,
  ImageTextAlternative,
  ImageToolbar,
  ImageUpload,
  Indent,
  IndentBlock,
  Italic,
  Link as CKEditorLink,
  LinkImage,
  List,
  ListProperties,
  // Markdown,
  // MediaEmbed,
  Mention,
  // PageBreak,
  Paragraph,
  PasteFromMarkdownExperimental,
  PasteFromOffice,
  RemoveFormat,
  ShowBlocks,
  SimpleUploadAdapter,
  SourceEditing,
  Strikethrough,
  Subscript,
  Superscript,
  Table,
  TableCaption,
  TableCellProperties,
  TableColumnResize,
  TableProperties,
  TableToolbar,
  TextTransformation,
  TodoList,
  Underline,
  WordCount,
  Plugin,
  ButtonView,
  Locale
} from 'ckeditor5';
import { MediaUploadAdapterPlugin } from '../media/MediaUploadAdapter';
import 'ckeditor5/ckeditor5.css';
import type { MediaFile, MediaImage } from '../../models/media';
import { useMediaDialog } from '../media/MediaDialogProvider';
import { useTheme } from '@mui/material/styles';

// Custom Media Plugin integrated into the component
class CustomMediaPlugin extends Plugin {
  static get pluginName(): 'CustomMedia' {
    return 'CustomMedia';
  }

  public init(): void {
    const editor = this.editor;
    const t = editor.t;

    editor.ui.componentFactory.add('customMedia', (locale: Locale) => {
      const view = new ButtonView(locale);

      view.set({
        label: t('Insert media'),
        icon: '<svg viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="M10 0C4.48 0 0 4.48 0 10s4.48 10 10 10 10-4.48 10-10S15.52 0 10 0zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z"/></svg>',
        tooltip: true
      });

      this.listenTo(view, 'execute', () => {
        this.openMediaSelector();
      });

      return view;
    });
  }

  private openMediaSelector(): void {
    const editor = this.editor;
    
    // Dispatch a custom event that will be handled by the React component
    const event = new CustomEvent('openMediaDialog', {
      detail: {
        onSelect: (file: MediaFile | MediaImage) => {
          if (file) {
            editor.execute('insertImage', { source: file.file });
          }
        },
        fileTypeFilter: 'images'
      }
    });
    
    document.dispatchEvent(event);
  }
}

interface CKRichEditorProps extends Omit<React.ComponentProps<typeof CKEditorType>, 'editor' | 'config' | 'onChange'> {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

const CKRichEditor: React.FC<CKRichEditorProps> = ({ 
  value, 
  onChange, 
  disabled = false,
  ...ckEditorProps 
}) => {
  const theme = useTheme();
  const editorContainerRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<HTMLDivElement>(null);
  const editorWordCountRef = useRef<HTMLDivElement>(null);
  const [isLayoutReady, setIsLayoutReady] = useState(false);
  const { openMediaDialog } = useMediaDialog();

  React.useEffect(() => {
    setIsLayoutReady(true);
    return () => setIsLayoutReady(false);
  }, []);

  // Listen for the custom event to open the media dialog
  React.useEffect(() => {
    const handleOpenMediaDialog = (event: CustomEvent) => {
      const { onSelect, fileTypeFilter } = event.detail;
      openMediaDialog({ onSelect, fileTypeFilter });
    };

    document.addEventListener('openMediaDialog', handleOpenMediaDialog as EventListener);
    return () => {
      document.removeEventListener('openMediaDialog', handleOpenMediaDialog as EventListener);
    };
  }, []);

  const { editorConfig } = useMemo(() => {
    if (!isLayoutReady) {
      return {};
    }

    return {
      editorConfig: {
        toolbar: {
          items: [
            'undo',
            'redo',
            '|',
            'sourceEditing',
            'showBlocks',
            'findAndReplace',
            'fullscreen',
            '|',
            'heading',
            '|',
            'fontSize',
            'fontFamily',
            'fontColor',
            'fontBackgroundColor',
            '|',
            'bold',
            'italic',
            'underline',
            'strikethrough',
            'subscript',
            'superscript',
            'code',
            'removeFormat',
            '|',
            'emoji',
            'horizontalLine',
            // 'pageBreak',
            'link',
            'insertImage',
            'customMedia', // Enable custom media button
            // 'insertImageViaUrl',
            // 'mediaEmbed',
            'insertTable',
            'highlight',
            'blockQuote',
            // 'codeBlock',
            // 'htmlEmbed',
            '|',
            'alignment',
            '|',
            'bulletedList',
            'numberedList',
            'todoList',
            'outdent',
            'indent'
          ],
          shouldNotGroupWhenFull: true
        },
        plugins: [
          Alignment,
          Autoformat,
          AutoImage,
          Autosave,
          BalloonToolbar,
          BlockQuote,
          Bold,
          Code,
          // CodeBlock,
          CustomMediaPlugin, // Add the custom media plugin
          Emoji,
          Essentials,
          FindAndReplace,
          FontBackgroundColor,
          FontColor,
          FontFamily,
          FontSize,
          Fullscreen,
          Heading,
          Highlight,
          HorizontalLine,
          // HtmlEmbed,
          ImageBlock,
          ImageCaption,
          ImageInline,
          ImageInsert,
          // ImageInsertViaUrl,
          ImageResize,
          ImageStyle,
          ImageTextAlternative,
          ImageToolbar,
          ImageUpload,
          Indent,
          IndentBlock,
          Italic,
          CKEditorLink,
          LinkImage,
          List,
          ListProperties,
          // Markdown,
          // MediaEmbed,
          Mention,
          // PageBreak,
          Paragraph,
          PasteFromMarkdownExperimental,
          PasteFromOffice,
          RemoveFormat,
          ShowBlocks,
          SimpleUploadAdapter,
          SourceEditing,
          Strikethrough,
          Subscript,
          Superscript,
          Table,
          TableCaption,
          TableCellProperties,
          TableColumnResize,
          TableProperties,
          TableToolbar,
          TextTransformation,
          TodoList,
          Underline,
          WordCount
        ],
        extraPlugins: [MediaUploadAdapterPlugin],
        balloonToolbar: ['bold', 'italic', '|', 'link', 'insertImage', '|', 'bulletedList', 'numberedList'],
        fontFamily: {
          supportAllValues: true
        },
        fontSize: {
          options: [10, 12, 14, 'default', 18, 20, 22],
          supportAllValues: true
        },
        fullscreen: {
          onEnterCallback: container =>
            container.classList.add(
              'editor-container',
              'editor-container_classic-editor',
              'editor-container_include-word-count',
              'editor-container_include-fullscreen',
              'main-container'
            )
        },
        heading: {
          options: [
            {
              model: 'paragraph',
              title: 'Paragraph',
              class: 'ck-heading_paragraph'
            },
            {
              model: 'heading1',
              view: 'h1',
              title: 'Heading 1',
              class: 'ck-heading_heading1'
            },
            {
              model: 'heading2',
              view: 'h2',
              title: 'Heading 2',
              class: 'ck-heading_heading2'
            },
            {
              model: 'heading3',
              view: 'h3',
              title: 'Heading 3',
              class: 'ck-heading_heading3'
            },
            {
              model: 'heading4',
              view: 'h4',
              title: 'Heading 4',
              class: 'ck-heading_heading4'
            },
            {
              model: 'heading5',
              view: 'h5',
              title: 'Heading 5',
              class: 'ck-heading_heading5'
            },
            {
              model: 'heading6',
              view: 'h6',
              title: 'Heading 6',
              class: 'ck-heading_heading6'
            }
          ]
        },
        image: {
          toolbar: [
            'toggleImageCaption',
            'imageTextAlternative',
            '|',
            'imageStyle:inline',
            'imageStyle:wrapText',
            'imageStyle:breakText',
            '|',
            'resizeImage'
          ],
          upload: {
            types: ['jpeg', 'png', 'gif', 'bmp', 'webp', 'tiff']
          }
        },
        initialData: value,
        licenseKey: 'GPL',
        link: {
          addTargetToExternalLinks: true,
          defaultProtocol: 'https://',
          decorators: {
            toggleDownloadable: {
              mode: 'manual',
              label: 'Downloadable',
              attributes: {
                download: 'file'
              }
            }
          }
        },
        list: {
          properties: {
            styles: true,
            startIndex: true,
            reversed: true
          }
        },
        mention: {
          feeds: [
            {
              marker: '@',
              feed: []
            }
          ]
        },
        menuBar: {
          isVisible: false
        },
        placeholder: 'Type or paste your content here!',
        table: {
          contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells', 'tableProperties', 'tableCellProperties']
        },
        isReadOnly: disabled,
        ui: {
          viewportOffset: {
            top: 0,
            right: 0,
            bottom: 0,
            left: 0
          }
        },
        ...(theme.palette.mode === 'dark' && {
          fontColor: {
            colors: [
              {
                color: 'hsl(0, 0%, 100%)',
                label: 'White'
              },
              {
                color: 'hsl(0, 0%, 0%)',
                label: 'Black'
              }
            ]
          },
          fontBackgroundColor: {
            colors: [
              {
                color: 'hsl(0, 0%, 0%)',
                label: 'Black'
              },
              {
                color: 'hsl(0, 0%, 100%)',
                label: 'White'
              }
            ]
          }
        })
      }
    };
  }, [isLayoutReady, value, disabled, theme.palette.mode]);

  return (
    <div className="main-container">
      <div
        className={`editor-container editor-container_classic-editor editor-container_include-word-count editor-container_include-fullscreen ${
          theme.palette.mode === 'dark' ? 'dark-mode' : ''
        }`}
        ref={editorContainerRef}
        style={{
          '--ck-color-base-background': theme.palette.mode === 'dark' ? theme.palette.background.paper : '#fff',
          '--ck-color-base-border': theme.palette.mode === 'dark' ? theme.palette.divider : '#ddd',
          '--ck-color-base-text': theme.palette.mode === 'dark' ? theme.palette.text.primary : '#333',
          '--ck-color-base-foreground': theme.palette.mode === 'dark' ? theme.palette.background.paper : '#fff',
          '--ck-color-toolbar-background': theme.palette.mode === 'dark' ? theme.palette.background.paper : '#f8f8f8',
          '--ck-color-toolbar-border': theme.palette.mode === 'dark' ? theme.palette.divider : '#ddd',
          '--ck-color-button-default-hover-background': theme.palette.mode === 'dark' ? theme.palette.action.hover : '#e6e6e6',
          '--ck-color-button-default-active-background': theme.palette.mode === 'dark' ? theme.palette.action.selected : '#d4d4d4',
          '--ck-color-button-on-background': theme.palette.mode === 'dark' ? theme.palette.text.primary : '#333',
          '--ck-color-button-on-hover-background': theme.palette.mode === 'dark' ? theme.palette.action.hover : '#e6e6e6',
          '--ck-color-button-on-active-background': theme.palette.mode === 'dark' ? theme.palette.action.selected : '#d4d4d4',
          '--ck-color-link': theme.palette.mode === 'dark' ? theme.palette.primary.main : '#1a75ff',
          '--ck-color-base-focus': theme.palette.mode === 'dark' ? theme.palette.primary.main : '#1a75ff',
          '--ck-color-base-selection': theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
          '--ck-color-base-selection-background': theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
          '--ck-color-base-selection-text': theme.palette.mode === 'dark' ? theme.palette.text.primary : '#333',
          '--ck-color-base-selection-border': theme.palette.mode === 'dark' ? theme.palette.primary.main : '#1a75ff',
          '--ck-color-base-selection-focus': theme.palette.mode === 'dark' ? theme.palette.primary.main : '#1a75ff',
          '--ck-color-base-selection-focus-background': theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
          '--ck-color-base-selection-focus-text': theme.palette.mode === 'dark' ? theme.palette.text.primary : '#333',
          '--ck-color-base-selection-focus-border': theme.palette.mode === 'dark' ? theme.palette.primary.main : '#1a75ff',
        } as React.CSSProperties}
      >
        <style>
          {`
            .ck.ck-editor__editable {
              background-color: ${theme.palette.mode === 'dark' ? theme.palette.background.paper : '#fff'} !important;
              color: ${theme.palette.mode === 'dark' ? theme.palette.text.primary : '#333'} !important;
            }
            .ck.ck-editor__editable p {
              color: ${theme.palette.mode === 'dark' ? theme.palette.text.primary : '#333'} !important;
            }
            .ck.ck-editor__editable h1,
            .ck.ck-editor__editable h2,
            .ck.ck-editor__editable h3,
            .ck.ck-editor__editable h4,
            .ck.ck-editor__editable h5,
            .ck.ck-editor__editable h6 {
              color: ${theme.palette.mode === 'dark' ? theme.palette.text.primary : '#333'} !important;
            }
            .ck.ck-editor__editable blockquote {
              border-left-color: ${theme.palette.mode === 'dark' ? theme.palette.divider : '#ddd'} !important;
              color: ${theme.palette.mode === 'dark' ? theme.palette.text.secondary : '#666'} !important;
            }
            .ck.ck-editor__editable code {
              background-color: ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'} !important;
              color: ${theme.palette.mode === 'dark' ? theme.palette.text.primary : '#333'} !important;
            }
            .ck.ck-editor__editable pre {
              background-color: ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'} !important;
              color: ${theme.palette.mode === 'dark' ? theme.palette.text.primary : '#333'} !important;
            }
            .ck.ck-editor__editable table {
              border-color: ${theme.palette.mode === 'dark' ? theme.palette.divider : '#ddd'} !important;
            }
            .ck.ck-editor__editable table td,
            .ck.ck-editor__editable table th {
              border-color: ${theme.palette.mode === 'dark' ? theme.palette.divider : '#ddd'} !important;
            }
          `}
        </style>
        <div className="editor-container__editor">
          <div ref={editorRef}>
            {editorConfig && (
              <CKEditor
                {...ckEditorProps}
                onReady={editor => {
                  const wordCount = editor.plugins.get('WordCount');
                  editorWordCountRef.current?.appendChild((wordCount as any).wordCountContainer);
                }}
                onAfterDestroy={() => {
                  Array.from(editorWordCountRef.current?.children || []).forEach(child => child.remove());
                }}
                editor={ClassicEditor}
                config={editorConfig as any}
                onChange={(_event: any, editor: any) => {
                  const data = editor.getData();
                  onChange(data);
                }}
              />
            )}
          </div>
        </div>
        <div className="editor_container__word-count" ref={editorWordCountRef}></div>
      </div>
    </div>
  );
};

export default CKRichEditor; 